import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CourseBanner from "../components/CourseBanner";

// ============================================================================
// SEO METADATA
// ============================================================================
const PAGE_URL = "https://mindbodyspirithygienetools.com/draw";
const PAGE_TITLE = "Free Card Pull · 58-Card Mind Body Spirit HYGIENE™ Deck · Temple Franklin";
const PAGE_DESC = "Pull a card right now from Temple Franklin's 58-card Mind Body Spirit HYGIENE™ deck. Free interactive daily card pull · one card, one message, one practice. Grounded daily ritual for the woman tapping in.";
const PAGE_IMAGE = "https://mindbodyspirithygienetools.com/cards/00-card-back.png";

const FAQ = [
  {
    q: "How do I pull a card?",
    a: "Take a breath. Set an intention or simply ask: what does my Tuesday need today? Click any card from the spread. It flips. You receive your message and your walk-it-out practice.",
  },
  {
    q: "Is this card pull free?",
    a: "Yes. Free. Forever. Pull one card every morning if you want. No login. No email required.",
  },
  {
    q: "Can I buy the physical deck?",
    a: "Yes — the 58-card print-ready deck is available. Each card is 2.75 x 4.75 inches, printed on premium card stock. Download all 58 PNG files at /card-downloads-58 or order through DriveThruCards.",
  },
  {
    q: "Are these cards a substitute for therapy?",
    a: "No. These cards are for inspiration, reflection, and a daily practice of tuning in. They are NOT a substitute for medical, psychological, financial, legal, or therapeutic advice. You have your own free will and your own discernment. Take what resonates and leave the rest.",
  },
];

const buildSchemas = () => [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": PAGE_URL,
    name: "Mind Body Spirit HYGIENE 58-Card Pull",
    url: PAGE_URL,
    description: PAGE_DESC,
    applicationCategory: "Lifestyle",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    author: { "@type": "Person", name: "Temple Franklin", url: "https://mindbodyspirithygienetools.com/about" },
    image: PAGE_IMAGE,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://mindbodyspirithygienetools.com/" },
      { "@type": "ListItem", position: 2, name: "Card Pulls", item: "https://mindbodyspirithygienetools.com/card-pulls" },
      { "@type": "ListItem", position: 3, name: "Pull Your Own Card", item: PAGE_URL },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  },
];

// ============================================================================
// THE DECK — 58 cards. Hand-written by Temple. Edit directly here.
// ============================================================================
const DECK = [
  // ──────── MIND (1–12) ────────
  { n: 1, title: "I GET TO", cat: "MIND", act: "NOW", body: "Every time you catch \"I have to\" on your tongue today — use \"I GET TO.\" Same task. Same Tuesday. Different woman standing in it.", walk: "Notice three \"I have to's\" before lunch. Reframe one out loud." },
  { n: 2, title: "Cut the Word TRY", cat: "MIND", act: "C", body: "\"I'll try\" is the polite cousin of self-abandonment. Yes is yes. No is no. Today, cut the word from your vocabulary.", walk: "When TRY shows up — ask: am I yes or no? Pick one." },
  { n: 3, title: "The Buck Stops Here", cat: "MIND", act: "A", body: "Whatever pattern walked into you from generations back — it does not have to walk out the same way. Today the buck stops here.", walk: "Name one inherited pattern you are refusing to pass forward." },
  { n: 4, title: "Awareness", cat: "MIND", act: "A", body: "First you have to see it. Not fix it. Not name it pretty. Just see what is.", walk: "Sit for 60 seconds. Ask — what am I actually feeling?" },
  { n: 5, title: "Clarity", cat: "MIND", act: "C", body: "Once you see it, get specific. Vague gets vague results.", walk: "Finish this sentence honestly — I actually want _______." },
  { n: 6, title: "Compassionate Curiosity", cat: "MIND", act: "A", body: "Not \"why am I like this?\" — that's a beating. Use \"what is this here to teach me?\" — that's curiosity.", walk: "Replace one self-critical thought with one curious one today." },
  { n: 7, title: "Start Small", cat: "MIND", act: "NOW", body: "Do not make BIG choices from trauma, fight, or flight. Start small. The small things compound.", walk: "What is the next right tiny step? Do that one." },
  { n: 8, title: "Take What Resonates", cat: "MIND", act: "T", body: "Not every truth is your truth on this day. Take what resonates. Leave the rest. The message lives in you.", walk: "One thing today — receive it. Decide if it's yours." },
  { n: 9, title: "Stop Abandoning Yourself", cat: "MIND", act: "A", body: "Every yes you don't mean is a chip off your power. Today — notice the abandoning. Stop the abandoning.", walk: "Where did you say yes when you meant no? Reset one of them." },
  { n: 10, title: "Reclaimed, Not Saved", cat: "MIND", act: "NOW", body: "No one is coming to save you. That is not a tragedy — that is a graduation. You reclaim yourself.", walk: "Name one thing today you are reclaiming. Out loud." },
  { n: 11, title: "The Messy Middle", cat: "MIND", act: "A", body: "You are not behind. You are not broken. You are in the messy middle. That is the holy place where the work gets done.", walk: "Forgive yourself for not being further along by now." },
  { n: 12, title: "Listen for the Lie", cat: "MIND", act: "A", body: "Every inherited story has a lie inside it. \"I'm too much.\" \"I'm not enough.\" Today, listen for the lie.", walk: "Catch one lie. Replace it with one truth. Write both down." },

  // ──────── BODY (13–24) ────────
  { n: 13, title: "Breathe", cat: "BODY", act: "T", body: "Four counts in. Hold four. Six counts out. Your nervous system follows your breath. The room did not change — but you did.", walk: "Three rounds. Right now." },
  { n: 14, title: "Ground", cat: "BODY", act: "T", body: "Feet on the floor. Hands on something solid. Name five things you see. The body remembers it is safe.", walk: "Do the 5-4-3-2-1 right now. Out loud if you can." },
  { n: 15, title: "Water", cat: "BODY", act: "NOW", body: "When in doubt, drink water. Your body is mostly water.", walk: "A full glass. Right now." },
  { n: 16, title: "Walk", cat: "BODY", act: "NOW", body: "Twenty minutes outside changes the shape of your day. Not a workout — a walk. Phone in pocket, not in hand.", walk: "Today. Twenty minutes. Door to door." },
  { n: 17, title: "Sleep Hygiene", cat: "BODY", act: "NOW", body: "What you do at 9pm decides who you wake up as at 6am. Phone down. Lights low. Body slow.", walk: "Pick one thing to do differently tonight." },
  { n: 18, title: "The Adrenal Truth", cat: "BODY", act: "A", body: "If you have been running on fumes — your body will eventually pick the brake for you. Slow down before it slows you.", walk: "One commitment you can release this week. Say it." },
  { n: 19, title: "Eat Real Food", cat: "BODY", act: "NOW", body: "Most days, how you feel started on a plate. No diet. No rules. Just one real meal.", walk: "Make one meal today from real, whole ingredients." },
  { n: 20, title: "The Body Is a Messenger", cat: "BODY", act: "T", body: "Your symptoms are not the problem. They are the messenger. Listen before you medicate. The body always knows.", walk: "What is your body here to say today? Write it." },
  { n: 21, title: "Stillness", cat: "BODY", act: "T", body: "You do not need to add another practice. You need stillness. Five minutes. No phone. Just sit.", walk: "Set a timer. Sit. That's it." },
  { n: 22, title: "Rest Is Work", cat: "BODY", act: "A", body: "Rest is not the reward for productivity. Rest IS the work. The world wants you tired so it can sell you energy.", walk: "Nap today. Without apology." },
  { n: 23, title: "Touch", cat: "BODY", act: "T", body: "Hand on your heart. Hand on your belly. Touch from your own hand is real medicine. The body knows the difference between abandoning and tending.", walk: "Five minutes. Hand on heart. Just breathe." },
  { n: 24, title: "The 50% Rule", cat: "BODY", act: "C", body: "If you are at 50% capacity today — make 50% your full effort. Do not stretch a depleted body. Pick the two essentials.", walk: "What are today's two? Do those. Drop the others." },

  // ──────── SPIRIT (25–36) ────────
  { n: 25, title: "Tapped In", cat: "SPIRIT", act: "T", body: "I tap in. I ground. I say hello. I say thank you. That is the entire ritual.", walk: "Three breaths. Then say hello to whoever guides you." },
  { n: 26, title: "Purple Chopsticks", cat: "SPIRIT", act: "T", body: "It could be God. It could be the angels. It could be the kitchen window light at 7am. Whatever lights you up — that's the Divine.", walk: "Today, notice where the light finds you. Thank it." },
  { n: 27, title: "Ask for a Sign", cat: "SPIRIT", act: "T", body: "If you need direction, ask. Then watch with your eyes wide open. The signs were never quiet — you were just not looking.", walk: "Ask for a specific sign. Then keep your head up today." },
  { n: 28, title: "Welcome Home", cat: "SPIRIT", act: "T", body: "You can come back to yourself any time. There is no late. There is no missed window. The door is open.", walk: "Welcome home." },
  { n: 29, title: "The Divine Team", cat: "SPIRIT", act: "T", body: "You are not doing this alone. Holy Spirit, angels, guides, ancestors — they are working with you.", walk: "Ask one of them for help today. By name. Out loud." },
  { n: 30, title: "Gratitude Is a Frequency", cat: "SPIRIT", act: "T", body: "Not the toxic-positivity kind. The \"I get to take out this trash because I have a home\" kind.", walk: "Three real gratitudes. Out loud. Specific." },
  { n: 31, title: "Prayer Is a Conversation", cat: "SPIRIT", act: "T", body: "Prayer is not begging. Prayer is sitting at the kitchen table with the Divine and saying — here is what's true.", walk: "Say one true thing to God today. The truer, the better." },
  { n: 32, title: "Trust What You Heard", cat: "SPIRIT", act: "T", body: "You asked. You heard. Now trust what you heard. The doubting is the work.", walk: "Act on the inner nudge that won't go away." },
  { n: 33, title: "Ancestors", cat: "SPIRIT", act: "T", body: "Some of your ancestors are working FOR you right now. The line did not die. It is praying you forward.", walk: "Thank one ancestor by name today." },
  { n: 34, title: "High Integrity", cat: "SPIRIT", act: "C", body: "When in doubt — ask: is this for the highest good of all? If yes, walk forward. If no, wait.", walk: "One decision today — run it through that question first." },
  { n: 35, title: "Surrender", cat: "SPIRIT", act: "T", body: "Surrender is not giving up. Surrender is putting down the hammer you've been swinging at God's plan.", walk: "One thing you are surrendering today. Write it. Burn it." },
  { n: 36, title: "I Am a Vessel", cat: "SPIRIT", act: "T", body: "I am not the source. I am the vessel. The minute I forget that, the work gets heavy.", walk: "Get out of the way today. Let what wants to come — come." },

  // ──────── HYGIENE (37–48) ────────
  { n: 37, title: "Daily · Non-Negotiable · Yours", cat: "HYGIENE", act: "NOW", body: "Mind, body, spirit hygiene is not a vibe. It is a practice. Daily. Non-negotiable. Yours.", walk: "Pick one HYGIENE practice. Do it before noon. Without excuse." },
  { n: 38, title: "Not Transformation", cat: "HYGIENE", act: "A", body: "You do not need to transform. You need to maintain hygiene. The same way you brush your teeth.", walk: "What is your spiritual equivalent of brushing your teeth? Do it." },
  { n: 39, title: "The PCC", cat: "HYGIENE", act: "C", body: "What are your non-negotiables — for yourself, by yourself? Write three. Live by them.", walk: "Today, pick one PCC line. Hold it without exception." },
  { n: 40, title: "The Morning Anchor", cat: "HYGIENE", act: "NOW", body: "Whatever your first twenty minutes are — those set the tone. Phone last. Breath first.", walk: "Tomorrow — no phone for the first twenty minutes." },
  { n: 41, title: "The Evening Review", cat: "HYGIENE", act: "A", body: "Three questions before sleep: What worked? What didn't? What will I foster tomorrow?", walk: "One sentence on each. Tonight. Before lights out." },
  { n: 42, title: "The Weekly Reset", cat: "HYGIENE", act: "C", body: "Once a week — sit with yourself. What needs to shift? Without judgment. Just data.", walk: "Sunday. Twenty minutes. With a pen. With yourself." },
  { n: 43, title: "Emergency Calm", cat: "HYGIENE", act: "T", body: "When the panic rises: hand on heart. Long exhale. Name what is true RIGHT NOW. The body can only hold one nervous system state at a time.", walk: "Three exhales. Each longer than the inhale." },
  { n: 44, title: "Clean House = Clean Mind", cat: "HYGIENE", act: "NOW", body: "The state of your space is the state of your mind made visible.", walk: "One room. Ten minutes. Reset." },
  { n: 45, title: "Social Media Hygiene", cat: "HYGIENE", act: "NOW", body: "Whatever you consume becomes your nervous system. Curate or be consumed.", walk: "Unfollow three accounts that drain you. Today. Without apology." },
  { n: 46, title: "Word Hygiene", cat: "HYGIENE", act: "A", body: "How you talk to yourself when no one is listening — that is the actual relationship of your life.", walk: "Catch one cruel inner sentence. Reframe it. Out loud." },
  { n: 47, title: "Tuesday Morning", cat: "HYGIENE", act: "NOW", body: "Tuesday morning is the real test. Not Sunday's prayer. Not Saturday's reset. Tuesday morning, when nothing is special.", walk: "Show up Tuesday the way you swore on Sunday." },
  { n: 48, title: "The Buck Stops Here (Hygiene)", cat: "HYGIENE", act: "A", body: "Whatever generational pattern walked into your hygiene — it does not have to walk out the same way. That ends with you.", walk: "Name one habit you are breaking for the line behind you." },

  // ──────── AGENCY (49–58) ────────
  { n: 49, title: "My No Is No", cat: "AGENCY", act: "C", body: "My no is no. It is not a negotiation. The explanation is for me. Not them.", walk: "One no today. Without softening it." },
  { n: 50, title: "Wait", cat: "AGENCY", act: "C", body: "Yes. No. Or wait. The WAIT is where the wisdom lives.", walk: "Anything anyone needs an answer to today — give them the WAIT if it's true." },
  { n: 51, title: "Who Is This For?", cat: "AGENCY", act: "A", body: "Before you do the thing — ask: who is this actually for? If the answer is \"them\" and you are depleted, stop.", walk: "One thing you were doing for them — put it down today." },
  { n: 52, title: "The Resentment Receipt", cat: "AGENCY", act: "A", body: "Resentment is the receipt for a yes you should have said no to. If you are resentful — go back. Find the original yes.", walk: "Name one resentment. Trace it. What was the yes underneath?" },
  { n: 53, title: "People-Pleasing Is Self-Abandonment", cat: "AGENCY", act: "A", body: "It is not kindness. It is self-abandonment in a softer voice.", walk: "One moment today — choose yourself over the polite version." },
  { n: 54, title: "No Explanation", cat: "AGENCY", act: "C", body: "The explanation is for me. Not them. You do not have to defend a boundary to make it real.", walk: "One boundary today. No defense. No softener." },
  { n: 55, title: "Not Your Job", cat: "AGENCY", act: "A", body: "Their healing is not your job. Their happiness is not your job. Their decisions are not your job.", walk: "Hand back one thing today that was never yours." },
  { n: 56, title: "Choose Yourself", cat: "AGENCY", act: "NOW", body: "Choose yourself the way you would choose a daughter you love. With fierce, gentle protection.", walk: "What would you do for your daughter today? Do it for you." },
  { n: 57, title: "They Mean Well", cat: "AGENCY", act: "A", body: "Some of the people who hurt you most — meant well. They simply did not know what they did not know.", walk: "One forgiveness offered today. Without requiring contact." },
  { n: 58, title: "Welcome Home", cat: "AGENCY", act: "NOW", body: "You are the elder you needed. You are the home you were searching for. There is no version of yourself you have to earn. You are already here.", walk: "Welcome home." },
];

// Category → color theme
const CAT_THEME = {
  MIND:    { bg: "#042F2E", text: "#efeae7", accent: "#d4af6a", eyebrow: "#d4af6a" },
  BODY:    { bg: "#efeae7", text: "#042F2E", accent: "#068fa2", eyebrow: "#25a1c2" },
  SPIRIT:  { bg: "linear-gradient(135deg, #042F2E 0%, #068fa2 100%)", text: "#efeae7", accent: "#d4af6a", eyebrow: "#d4af6a" },
  HYGIENE: { bg: "#25a1c2", text: "#042F2E", accent: "#042F2E", eyebrow: "#042F2E" },
  AGENCY:  { bg: "#efeae7", text: "#042F2E", accent: "#d4af6a", eyebrow: "#d4af6a" },
};

const CardDraw = () => {
  const [drawn, setDrawn] = useState(null);
  const [flipped, setFlipped] = useState(false);

  // Inject SEO metadata + JSON-LD schemas (cleaned up on unmount)
  useEffect(() => {
    document.title = PAGE_TITLE;

    const upsert = (key, attr, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsert("description", "name", PAGE_DESC);
    upsert(
      "keywords",
      "name",
      "free card pull, daily card pull, online tarot, free tarot reading, oracle card, interactive card deck, daily affirmation card, spiritual practice, Temple Franklin, Mind Body Spirit HYGIENE, free oracle reading, mind body spirit deck"
    );
    upsert("og:type", "property", "website");
    upsert("og:title", "property", PAGE_TITLE);
    upsert("og:description", "property", PAGE_DESC);
    upsert("og:url", "property", PAGE_URL);
    upsert("og:image", "property", PAGE_IMAGE);
    upsert("og:site_name", "property", "Mind Body Spirit HYGIENE™ Tools");
    upsert("twitter:card", "name", "summary_large_image");
    upsert("twitter:title", "name", PAGE_TITLE);
    upsert("twitter:description", "name", PAGE_DESC);
    upsert("twitter:image", "name", PAGE_IMAGE);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", PAGE_URL);

    const scripts = buildSchemas().map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-draw-seo", "1");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.querySelectorAll('script[data-draw-seo="1"]').forEach((s) => s.remove());
    };
  }, []);

  const pullCard = () => {
    const idx = Math.floor(Math.random() * DECK.length);
    setDrawn(DECK[idx]);
    setFlipped(false);
    setTimeout(() => setFlipped(true), 200);
  };

  const pullAgain = () => {
    setFlipped(false);
    setTimeout(pullCard, 350);
  };

  const theme = drawn ? CAT_THEME[drawn.cat] : CAT_THEME.MIND;

  return (
    <div className="min-h-screen bg-[#efeae7]" data-testid="draw-page">
      {/* HERO */}
      <section className="pt-24 pb-8 px-6 md:pt-32 text-center" data-testid="draw-hero">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="draw-eyebrow">
            The 58-Card Mind Body Spirit HYGIENE™ Deck
          </p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter text-[#042F2E] leading-[0.95] mb-6" data-testid="draw-headline">
            Pull a card.<br />
            <span className="italic text-[#25a1c2]">Tap in.</span>
          </h1>
          <p className="font-serif italic text-xl text-[#068fa2] max-w-2xl mx-auto mb-10" data-testid="draw-sub">
            One card. One message. Yours if it lands.
          </p>
        </div>
      </section>

      {/* CARD STAGE */}
      <section className="px-6 pb-16" data-testid="card-stage">
        <div className="max-w-2xl mx-auto">
          {/* CARD ITSELF */}
          <div
            className="relative mx-auto"
            style={{ perspective: "2000px", maxWidth: "440px" }}
            data-testid="card-container"
          >
            <div
              className="relative w-full transition-transform duration-700 ease-out"
              style={{
                transformStyle: "preserve-3d",
                transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                aspectRatio: "2 / 3",
              }}
              data-testid="card-flipper"
            >
              {/* CARD BACK */}
              <div
                className="absolute inset-0 rounded-3xl shadow-2xl flex flex-col items-center justify-center p-8"
                style={{
                  backfaceVisibility: "hidden",
                  background: "linear-gradient(135deg, #042F2E 0%, #068fa2 100%)",
                  border: "2px solid #d4af6a",
                }}
                data-testid="card-back"
              >
                <div className="text-[#d4af6a] text-5xl mb-4">✦</div>
                <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#d4af6a] mb-3">
                  Mind Body Spirit
                </p>
                <p className="font-serif italic text-3xl text-[#efeae7] mb-3">
                  HYGIENE<sup className="text-sm">™</sup>
                </p>
                <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#d4af6a]">
                  58 Card Deck
                </p>
                <div className="mt-6 text-[#d4af6a] text-2xl">✦</div>
              </div>

              {/* CARD FACE */}
              {drawn && (
                <div
                  className="absolute inset-0 rounded-3xl shadow-2xl flex flex-col p-8 md:p-10"
                  style={{
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                    background: theme.bg,
                    color: theme.text,
                    border: `1px solid ${theme.accent}55`,
                  }}
                  data-testid="card-face"
                >
                  {/* top row */}
                  <div className="flex justify-between items-start mb-4">
                    <span
                      className="font-sans text-[10px] tracking-[0.35em] uppercase font-semibold"
                      style={{ color: theme.eyebrow }}
                      data-testid="card-category"
                    >
                      {drawn.cat}
                    </span>
                    <span
                      className="font-serif italic text-base opacity-70"
                      data-testid="card-number"
                    >
                      № {drawn.n}
                    </span>
                  </div>

                  {/* ornamental rule */}
                  <div className="flex items-center gap-3 mb-5">
                    <span className="block h-px flex-1" style={{ backgroundColor: theme.accent, opacity: 0.4 }} />
                    <span style={{ color: theme.accent }}>✦</span>
                    <span className="block h-px flex-1" style={{ backgroundColor: theme.accent, opacity: 0.4 }} />
                  </div>

                  {/* title */}
                  <h2
                    className="font-serif italic leading-[1.05] mb-5"
                    style={{ fontSize: "clamp(1.6rem, 5vw, 2.4rem)", color: theme.text }}
                    data-testid="card-title"
                  >
                    {drawn.title}
                  </h2>

                  {/* body */}
                  <p
                    className="font-serif leading-relaxed mb-5"
                    style={{ fontSize: "clamp(0.9rem, 2vw, 1.05rem)", opacity: 0.9 }}
                    data-testid="card-body"
                  >
                    {drawn.body}
                  </p>

                  {/* walk it out */}
                  <div
                    className="mt-auto pt-4 border-t"
                    style={{ borderColor: theme.accent + "33" }}
                  >
                    <p
                      className="font-sans text-[9px] tracking-[0.35em] uppercase font-semibold mb-2"
                      style={{ color: theme.eyebrow }}
                    >
                      Walk it out
                    </p>
                    <p
                      className="font-serif italic leading-snug mb-3"
                      style={{ fontSize: "clamp(0.85rem, 1.8vw, 0.95rem)", opacity: 0.85 }}
                      data-testid="card-walk"
                    >
                      {drawn.walk}
                    </p>
                    <p
                      className="font-serif italic text-right"
                      style={{ fontSize: "clamp(0.8rem, 1.6vw, 0.9rem)", color: theme.accent }}
                      data-testid="card-signature"
                    >
                      — Temple
                    </p>
                  </div>

                  {/* A.C.T. NOW tag */}
                  <div className="absolute top-4 right-4" style={{ display: "none" }} aria-hidden="true">
                    {drawn.act}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* BUTTON ROW */}
          <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="draw-buttons">
            {!drawn && (
              <button
                onClick={pullCard}
                className="bg-[#25a1c2] hover:bg-[#068fa2] text-white font-sans font-semibold px-9 py-4 rounded-full text-base tracking-wide transition-colors shadow-lg"
                data-testid="btn-pull-first"
              >
                Pull a card →
              </button>
            )}
            {drawn && (
              <>
                <button
                  onClick={pullAgain}
                  className="bg-[#25a1c2] hover:bg-[#068fa2] text-white font-sans font-semibold px-8 py-3 rounded-full text-sm tracking-wide transition-colors"
                  data-testid="btn-pull-again"
                >
                  Pull another →
                </button>
                <Link
                  to="/card-pulls"
                  className="border border-[#042F2E] hover:bg-[#042F2E] hover:text-white text-[#042F2E] font-sans font-semibold px-8 py-3 rounded-full text-sm tracking-wide transition-colors"
                  data-testid="btn-watch-temple"
                >
                  Watch today's pull on YouTube
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Post-pull: $11 I GET TO Self-Guided Course upsell */}
      {drawn && (
        <section className="px-6 py-12" style={{ background: "#F8F5F3" }} data-testid="card-draw-course-upsell">
          <CourseBanner product="course-1" variant="card" testId="card-draw-course-banner" />
        </section>
      )}

      {/* DECK DESCRIPTION */}
      <section className="py-14 px-6 bg-white" data-testid="deck-about">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium mb-5 text-center">
            About the deck
          </p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-[#042F2E] text-center leading-tight mb-8">
            58 cards. Five categories.<br />
            <span className="italic text-[#25a1c2]">One daily practice.</span>
          </h2>

          <div className="grid md:grid-cols-5 gap-3 mb-10">
            {[
              { cat: "MIND", n: 12, bg: "#042F2E", fg: "#efeae7" },
              { cat: "BODY", n: 12, bg: "#efeae7", fg: "#042F2E", border: "#068fa2" },
              { cat: "SPIRIT", n: 12, bg: "linear-gradient(135deg, #042F2E 0%, #068fa2 100%)", fg: "#efeae7" },
              { cat: "HYGIENE", n: 12, bg: "#25a1c2", fg: "#042F2E" },
              { cat: "AGENCY", n: 10, bg: "#efeae7", fg: "#042F2E", border: "#d4af6a" },
            ].map((c) => (
              <div
                key={c.cat}
                className="rounded-xl p-5 text-center"
                style={{
                  background: c.bg,
                  color: c.fg,
                  border: c.border ? `1px solid ${c.border}` : "none",
                }}
                data-testid={`cat-${c.cat.toLowerCase()}`}
              >
                <div className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-1">{c.cat}</div>
                <div className="font-serif text-3xl">{c.n}</div>
              </div>
            ))}
          </div>

          <p className="text-[#068fa2] leading-relaxed text-[16px] font-serif italic text-center max-w-2xl mx-auto">
            Each card is hand-written in my voice — short, grounded, and tied to the A.C.T. NOW Method™. Pull one a day. Read it out loud. Walk it out. That's the practice.
          </p>
        </div>
      </section>

      {/* PHYSICAL DECK WAITLIST */}
      <section className="py-14 px-6" data-testid="physical-waitlist">
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-[#042F2E] via-[#068fa2] to-[#25a1c2] rounded-3xl p-10 md:p-14 text-white text-center">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#5EEAD4] font-medium mb-5">
            Coming soon · Printed deck
          </p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tighter mb-5 leading-tight">
            A real deck in your hands.<br />
            <span className="italic">Pull one with your morning coffee.</span>
          </h2>
          <p className="font-serif italic text-lg text-white/85 mb-7 max-w-xl mx-auto">
            I'm working on the printed companion deck — 58 cards, branded, beautifully boxed. Join the waitlist and you'll be first to know when it ships.
          </p>
          <Link
            to="/workbook?from=card-draw"
            className="inline-block bg-white text-[#042F2E] hover:bg-[#5EEAD4] font-sans font-semibold px-8 py-4 rounded-full text-sm tracking-wide transition-colors"
            data-testid="cta-waitlist"
          >
            Join the waitlist →
          </Link>
        </div>
      </section>

      {/* FREE WILL DISCLAIMER */}
      <section className="py-12 px-6" data-testid="draw-disclaimer">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-serif italic text-[#068fa2] leading-relaxed text-[15px]">
            Take what resonates and leave the rest. You have your own free will and discernment. These messages are offerings — not guarantees. Not a substitute for medical, psychological, financial, or therapeutic advice. Always in high integrity. Always in Divine light.
          </p>
          <p className="font-serif text-[#042F2E] mt-4">— Temple</p>
        </div>
      </section>
    </div>
  );
};

export default CardDraw;
